import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { avatarGradient, brandColor, brandInitial, brandOf } from '../utils/brand'
import { initialsOf } from '../utils/text'
import { loadVcards } from '../services/vcardStore'
import { loadBrands } from '../services/brandStore'
import { loadAccounts } from '../services/accountStore'
import type { Account, Brand, Role, Vcard } from '../types'

interface CoverContent {
  eyebrow: string
  /** [Vortext, hervorgehobenes Wort, Nachtext]. */
  titleParts: [string, string, string]
  text: string
}

function coverFor(role: Role, brandName?: string): CoverContent {
  if (role === 'admin') {
    return {
      eyebrow: 'Administration',
      titleParts: ['Alle ', 'Marken', ' & vCards verwalten'],
      text: 'Als Admin verwaltest du sämtliche Brands und legst vCards an — dabei entsteht automatisch das Nutzerkonto (E-Mail = Login).',
    }
  }
  return {
    eyebrow: brandName || 'Deine Brand',
    titleParts: ['Deine ', 'Brand', ' pflegen'],
    text: 'Als Brand-Admin bearbeitest du dein Brand-Profil und die vCards deiner Marke. Neue vCards/Nutzer legt der Admin an.',
  }
}

interface DashboardData {
  vcards: Vcard[]
  brands: Brand[]
  accounts: Account[]
}

/** Rollen-Dashboard (Cover + Stat-Cards + „Zuletzt bearbeitet"), Daten aus den
 *  F4-Stores (echte API bzw. DEV-Mock-Fallback). BMC-Designsystem `console.css`. */
export function DashboardPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)

  useEffect(() => {
    if (!session) return
    let cancelled = false
    Promise.all([loadVcards(session), loadBrands(session), loadAccounts(session)])
      .then(([vcards, brands, accounts]) => {
        if (!cancelled) setData({ vcards, brands, accounts })
      })
      .catch(() => {
        if (!cancelled) setData({ vcards: [], brands: [], accounts: [] })
      })
    return () => {
      cancelled = true
    }
  }, [session])

  if (!session) return null

  if (!data) {
    return (
      <section className="panel">
        <div className="head">
          <h2>Dashboard</h2>
        </div>
        <div className="empty">Lädt …</div>
      </section>
    )
  }

  const { vcards, brands, accounts } = data
  const activeCount = vcards.filter((v) => v.active).length
  const brand = session.brandKey ? brandOf(brands, session.brandKey) : null
  const cover = coverFor(session.role, brand?.brandName)
  const [pre, hl, post] = cover.titleParts

  const activePct = vcards.length ? (activeCount / vcards.length) * 100 : 0
  const thirdLabel = session.role === 'admin' ? 'Brands' : 'Nutzer'
  const thirdValue = session.role === 'admin' ? brands.length : accounts.length

  const gate = can(session)

  return (
    <>
      <section className="cover">
        <div className="eyebrow">{cover.eyebrow}</div>
        <h1>
          {pre}
          <span className="hl">{hl}</span>
          {post}
        </h1>
        <p>{cover.text}</p>
      </section>

      <section className="stats">
        <div className="stat b1">
          <div className="lbl">vCards</div>
          <div className="val">{vcards.length}</div>
          <div className="bar">
            <span style={{ width: '100%' }} />
          </div>
        </div>
        <div className="stat b2">
          <div className="lbl">Aktiv</div>
          <div className="val">{activeCount}</div>
          <div className="bar">
            <span style={{ width: `${activePct}%` }} />
          </div>
        </div>
        <div className="stat b3">
          <div className="lbl">{thirdLabel}</div>
          <div className="val">{thirdValue}</div>
          <div className="bar">
            <span style={{ width: '70%' }} />
          </div>
        </div>
        <div className="stat b4">
          <div className="lbl">Nutzer</div>
          <div className="val">{accounts.length || '–'}</div>
          <div className="bar">
            <span style={{ width: '60%' }} />
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="head">
          <h2>Zuletzt bearbeitet</h2>
          <span className="sub">{vcards.length} Einträge</span>
          <span className="spacer" />
          {gate.vcardCreate() && (
            <button type="button" className="btn primary sm" onClick={() => navigate('/editor/vcards/new')}>
              {Icons.plus} Neue vCard
            </button>
          )}
          <button type="button" className="btn ghost sm" onClick={() => navigate('/admin/vcards')}>
            Alle vCards →
          </button>
        </div>
        <div className="list">
          {vcards.length === 0 ? (
            <div className="empty">Keine vCards in deinem Bereich.</div>
          ) : (
            vcards.map((vc) => {
              const b = brandOf(brands, vc.brandKey)
              const name = vc.firstName || vc.lastName ? `${vc.firstName ?? ''} ${vc.lastName ?? ''}`.trim() : 'Neue vCard'
              return (
                <div className="lrow" key={vc.urlKey}>
                  <span className="pic" style={{ background: avatarGradient(vc.urlKey) }}>
                    {initialsOf(vc.firstName, vc.lastName)}
                  </span>
                  <div className="who">
                    <div className="nm">{name}</div>
                    <div className="mt">
                      {vc.role || '—'} · <span className="path">/{vc.urlKey}</span>
                    </div>
                  </div>
                  <div className="side">
                    {session.role === 'admin' && (
                      <span className="chip-brand" style={{ background: brandColor(b) }} title={b.brandName}>
                        {brandInitial(b)}
                      </span>
                    )}
                    <span className={`badge ${vc.active ? 'active' : 'inactive'}`}>
                      {vc.active && <span className="dot" />}
                      {vc.active ? 'aktiv' : 'inaktiv'}
                    </span>
                    <span className="row-go">{Icons.go}</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
    </>
  )
}
